import {
  ALERT_TEMPLATE, ASHI_WASUU, ASHI_WASUU_KANA,
  CHINUTSU, HEISHI_WASUU, HEISHI_KANA,
  KETSUBAN, KIKEI_WASUU, KIKEI_WASUU_KANA, KUCHI_WASUU, KUCHI_WASUU_KANA,
  NAKI, OSU_WASUU, OSU_WASUU_KANA, OTHER_WASUU, OTHER_WASUU_KANA, SHOU_WASUU, SHOU_WASUU_KANA,
  SONOTA, SEIJOU, MIHOUKOKU, HOUKOKUZUMI, TOUYO_HOUHOUS, EVENT_SHUBETSU_LIST, WEEK, OTHER, IKUSUU, IKUSEI, DOCUMENT_NAME
} from './constants';
import {ShiyoulotV2} from './interfaces/shiyoulot';

/**
 * type alias 一覧
 */

export type Weather = 'fine' | 'cloudy' | 'rainy' | 'snowy' | 'windy';

export type HinaHealth = 'fine' | 'poor';

export type YesNo = 'no' | 'yes';
export type OsuMesu = 'オス' | 'メス';
export type Shiyuu = 'm' | 'f';

const alertShubetsu = ALERT_TEMPLATE.map(row => row.shubetsu)[0];
export type ALERT_SHUBETSU = typeof alertShubetsu;

export type ABNORMAL_SHUBETSU = typeof NAKI | typeof KETSUBAN | typeof CHINUTSU | typeof SONOTA;
export type ABNORMAL_SHUBETSU_WITH_SEIJOU = ABNORMAL_SHUBETSU | typeof SEIJOU;
export type ABNORMAL_SHUBETSU_ALL = ABNORMAL_SHUBETSU_WITH_SEIJOU | typeof MIHOUKOKU | typeof HOUKOKUZUMI;

export type HeishiToutaProperties = typeof OSU_WASUU | typeof ASHI_WASUU | typeof KUCHI_WASUU | typeof KIKEI_WASUU | typeof SHOU_WASUU | typeof OTHER_WASUU | typeof HEISHI_WASUU;
export type HeishiToutaPropertiesKana = typeof OSU_WASUU_KANA | typeof ASHI_WASUU_KANA | typeof KUCHI_WASUU_KANA | typeof KIKEI_WASUU_KANA | typeof SHOU_WASUU_KANA | typeof OTHER_WASUU_KANA | typeof HEISHI_KANA;

const touyoHouhous = TOUYO_HOUHOUS.map(row => row.name)[0];
export type TouyoHouhous = typeof touyoHouhous;

const eventShubetsu = EVENT_SHUBETSU_LIST.map(row => row.eventShubetsu)[0];
export type EventShubetsu = typeof eventShubetsu;

// その他を除いたイベント種別
export type EventShubetsuExceptOther = Exclude<EventShubetsu, typeof OTHER>;

const eventShubetsuName = EVENT_SHUBETSU_LIST.map(row => row.eventShubetsuName)[0];
export type EventShubetsuName = typeof eventShubetsuName;

const week = WEEK.map(row => row)[0];
export type Week = typeof week;

export interface LayoutConfig {
  row: number,
  column: number,
  stage: number,
  floor: number,
}

export type ToDelete = { toDelete?: boolean };

// 特定のプロパティをoptionalに変更 (例：id: string → id? : string)
export type WithOptional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

// 特定のプロパティをrequiredに変更 (例：id?: string → id : string)
export type NotOptional<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;

// 特定のプロパティ以外をoptionalに変更
export type exceptKeyWithOptional<T, K extends keyof T> = Pick<T, K> & Partial<Omit<T, K>>;

// 特定のプロパティをnull許容(例： jisshiDate: string → jisshiDate: string | null)
export type WithNullable<T, K extends keyof T> = Omit<T, K> & { [K in keyof T]: T[K] | null };

// 特定のプロパティをnull許容しない(例： jisshiDate: string | null → jisshiDate: string)
export type WithoutNullable<T, K extends keyof T> = Omit<T, K> & { [K in keyof T]: NonNullable<T[K]> };

// readonly を mutable に変更
export type Mutable<Type> = {
  -readonly [Key in keyof Type]: Type[Key];
};

// イベント用
export type WithChecked<T> = T & { checked?: boolean };
export type WithHover<T> = T & { hover?: boolean };
// 実施日null許容しない
export type JisshiDateNonNullable<T extends {jisshiDate: string | null}> = Omit<T, 'jisshiDate'> & { 'jisshiDate': NonNullable<T['jisshiDate']> };

export type EventForEdit<T> = Partial<T> & { hover?: boolean, toDelete?: boolean };
export type KeishaShubetsu = typeof IKUSUU | typeof IKUSEI;
export type ShiyoulotForEdit = ShiyoulotV2 & { keishaShubetsu?: KeishaShubetsu }

// https://zenn.dev/k_u_0615/articles/870d0d79fd6277
// objectのunion型から型を抽出する
export type FindFromUnion<
  Target extends Record<string, any>,
  KeyProp extends keyof Target,
  Key extends Target[KeyProp]
  > = Target extends { [x in KeyProp]: Key } ? Target : never;

/**
 * ex. 「123-S」
 * REGEX_SHIYOULOT_KEY を参照
 */
export type ShiyoulotKey = string;
// yyyy-MM-dd型の日付文字列
export type yyyyMMdd = string;

export interface KeyString<T> {
  [key: string]: T
}

//#region 羽数関連
export interface HeishiWasuu {
  heishiWasuu: number | 0,  // 斃死羽数
}

export interface ToutaWasuu {
  toutaWasuu: number | 0,  // 淘汰羽数
}

export interface ToutaWasuuDetail {
  ashiWasuu: number | 0,  // 脚弱・脚折れ
  kikeiWasuu: number | 0, // 奇形
  kuchiWasuu: number | 0, // 口曲がり
  osuWasuu: number | 0, // ♂混ざり・誤鑑
  otherWasuu: number | 0, // その他淘汰
  shouWasuu: number | 0,  // 未熟・小さい個体
}

export interface TeiseiWasuu {
  teiseiWasuu: number | 0,  // 訂正羽数
}

export interface GenzaiWasuu {
  genzaiWasuu: number | 0,  // 現在羽数
}

export interface HeishiToutaWasuu extends HeishiWasuu, ToutaWasuu{}
export interface HeishiToutaTeiseiWasuu extends HeishiToutaWasuu, TeiseiWasuu{}
//#endregion

// document 番号が連番になっているものは、
// masters collection 配下に、document 明細の中で最大の document 番号を示す document を配置
export type TargetDocName = typeof DOCUMENT_NAME.SHIYOULOT_NUMBER | typeof DOCUMENT_NAME.CHUMON_NUMBER;
